import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Beaker, Search, Clock, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BarBasics() {
  const { getAuthHeaders } = useAuth();
  const [basics, setBasics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [basicsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/bar-basics`),
          axios.get(`${API}/bar-basics/categories/list`)
        ]);
        
        setBasics(basicsRes.data);
        setCategories(categoriesRes.data.categories);
      } catch (error) {
        console.error('Error fetching bar basics:', error);
        toast.error('Failed to load bar basics');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredBasics = basics.filter(b => {
    const matchesSearch = search === '' || 
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      b.ingredients.some(i => i.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = selectedCategory === '' || b.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const categoryLabels = {
    'syrups': 'Syrups & Sweeteners',
    'mixers': 'Mixers & Juices',
    'techniques': 'Techniques',
    'garnishes': 'Garnishes'
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="bar-basics-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-[#F5F5F5] mb-3 flex items-center gap-3">
          <Beaker className="w-10 h-10 text-[#D4AF37]" />
          Bar Basics
        </h1>
        <p className="text-[#A3A3A3] text-lg">
          Learn to make essential syrups, mixers, and garnishes
        </p>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#525252]" />
          <Input
            type="text"
            placeholder="Search recipes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10 focus:border-[#D4AF37] text-[#F5F5F5] placeholder:text-[#525252] rounded-xl"
            data-testid="search-input"
          />
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={selectedCategory === '' ? 'default' : 'outline'}
            onClick={() => setSelectedCategory('')}
            className={selectedCategory === '' 
              ? 'bg-[#D4AF37] text-black hover:bg-[#F4CF57]' 
              : 'border-white/10 text-[#A3A3A3] hover:text-[#F5F5F5]'}
            size="sm"
          >
            All
          </Button>
          {categories.map(cat => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              onClick={() => setSelectedCategory(cat)}
              className={selectedCategory === cat 
                ? 'bg-[#D4AF37] text-black hover:bg-[#F4CF57]' 
                : 'border-white/10 text-[#A3A3A3] hover:text-[#F5F5F5]'}
              size="sm"
            >
              {categoryLabels[cat] || cat}
            </Button>
          ))}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 shimmer rounded-2xl" />
          ))}
        </div>
      ) : filteredBasics.length === 0 ? (
        <div className="empty-state glass rounded-2xl py-16">
          <Beaker className="w-12 h-12 text-[#525252] mb-4" />
          <h3 className="text-lg font-bold text-[#F5F5F5] mb-2">No recipes found</h3>
          <p className="text-[#A3A3A3]">Try adjusting your search</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBasics.map((basic, index) => (
            <div
              key={basic.id}
              className="bg-[#121212] border border-white/5 rounded-2xl overflow-hidden card-lift animate-fadeIn"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* Header */}
              <button
                onClick={() => toggleExpand(basic.id)}
                className="w-full p-5 flex items-center gap-4 text-left"
                data-testid={`expand-btn-${basic.id}`}
              >
                <img
                  src={basic.image_url}
                  alt={basic.name}
                  className="w-16 h-16 rounded-xl object-cover"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-[#F5F5F5]">{basic.name}</h3>
                    <span className="category-badge text-xs">
                      {categoryLabels[basic.category] || basic.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-[#A3A3A3]">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {basic.shelf_life}
                    </span>
                    <span>{basic.ingredients.length} ingredients</span>
                  </div>
                </div>
                {expandedId === basic.id ? (
                  <ChevronUp className="w-5 h-5 text-[#A3A3A3]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#A3A3A3]" />
                )}
              </button>

              {/* Expanded Content */}
              {expandedId === basic.id && (
                <div className="px-5 pb-5 pt-0 border-t border-white/5 animate-fadeIn">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
                    {/* Ingredients */}
                    <div>
                      <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wide mb-3">
                        Ingredients
                      </h4>
                      <ul className="space-y-2">
                        {basic.ingredients.map((ing, i) => (
                          <li key={i} className="flex items-center gap-2 text-[#F5F5F5]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                            {ing}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Instructions */}
                    <div>
                      <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wide mb-3">
                        Instructions
                      </h4>
                      <p className="text-[#A3A3A3] leading-relaxed">
                        {basic.instructions}
                      </p>
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="mt-6 p-4 rounded-xl bg-[#D4AF37]/5 border border-[#D4AF37]/20">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                      <div>
                        <h4 className="text-sm font-bold text-[#D4AF37] mb-1">Pro Tip</h4>
                        <p className="text-[#A3A3A3] text-sm">{basic.tips}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
